import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ocrTemplatesApi } from './ocr-templates.api';
import { ocrTemplateKeys } from './ocr-templates.queries';
import { fieldHistoryKeys } from './field-history.queries';
import type {
  CreateOcrTemplateRequest,
  UpdateOcrTemplateRequest,
  SyncCustomFieldsRequest,
  SyncFieldRulesRequest,
  OcrTemplatesListParams,
  GenerateDescriptionRequest,
  OcrTemplateImportResult
} from '../types';

export function useCreateOcrTemplateMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: (data: CreateOcrTemplateRequest) =>
      ocrTemplatesApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.lists() });
    }
  });
}

export function useUpdateOcrTemplateMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number;
      data: UpdateOcrTemplateRequest;
    }) => ocrTemplatesApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
    }
  });
}

export function useDeleteOcrTemplateMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: (id: number) => ocrTemplatesApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.lists() });
    }
  });
}

export function useBulkDeleteOcrTemplatesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<OcrTemplatesListParams> }
    ) => ocrTemplatesApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
    }
  });
}

export function useSyncCustomFieldsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SyncCustomFieldsRequest }) =>
      ocrTemplatesApi.syncCustomFields(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('customFieldsSynced')
    },
    // TWO cache roots, because the field list is rendered by two of them.
    // `fieldHistoryKeys` is not nested under `ocrTemplateKeys`, so the
    // template invalidation below does not reach it — and a sync is exactly
    // the write that invalidates history: renaming a field changes its machine
    // `name`, which IS the key every sighting is recorded under, so the
    // per-field history dialog would keep answering for the OLD key (all
    // zeros, as if the field had never been extracted) until the cache
    // expired. Same shape as the discovered-field decision bug — see
    // `discoveredFieldDecisionInvalidationKeys()` and
    // `frontend/src/features/CLAUDE.md` -> "Mutation invalidation".
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
      queryClient.invalidateQueries({
        queryKey: fieldHistoryKeys.forTemplate(variables.id)
      });
    }
  });
}

export function useSyncFieldRulesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SyncFieldRulesRequest }) =>
      ocrTemplatesApi.syncFieldRules(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('fieldRulesSynced')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
    }
  });
}

/**
 * OMA-637 — put an archived field back into the template's live field set.
 *
 * `skipGlobalErrorToast` is deliberately NOT set: a restore is refused when a
 * live field already holds the same key or label, and that 422 carries the
 * offending field's name — which is the whole answer the manager needs.
 */
export function useArchiveFieldMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: ({ id, fieldId }: { id: number; fieldId: number }) =>
      ocrTemplatesApi.archiveField(id, fieldId),
    // No operationType: archiving is a green success, and the global error
    // toast stays on so a refusal (a system field) shows the server's reason.
    meta: { fallbackSuccessMessage: t('archiveFieldSuccess') },
    // Same two roots as restore: the field leaves the editor's list and
    // stops being extracted, so its history view changes too.
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
      queryClient.invalidateQueries({
        queryKey: fieldHistoryKeys.forTemplate(variables.id)
      });
    }
  });
}

export function useRestoreArchivedFieldMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: ({ id, fieldId }: { id: number; fieldId: number }) =>
      ocrTemplatesApi.restoreArchivedField(id, fieldId),
    meta: { fallbackSuccessMessage: t('restoreFieldSuccess') },
    // Same two roots as the sync above, and for the same reason: the restored
    // field re-enters the editor's list AND starts being extracted again, so
    // its per-field history is live once more.
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
      queryClient.invalidateQueries({
        queryKey: fieldHistoryKeys.forTemplate(variables.id)
      });
    }
  });
}

/**
 * OMA-671 — set the percentage an archived field suppresses later
 * discoveries at. Only the template caches move: the change alters what the
 * next run extracts, not anything already listed elsewhere.
 */
export function useUpdateArchivedFieldThresholdMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation({
    mutationFn: ({
      id,
      fieldId,
      matchThreshold
    }: {
      id: number;
      fieldId: number;
      matchThreshold: number | null;
    }) =>
      ocrTemplatesApi.updateArchivedFieldThreshold(id, fieldId, matchThreshold),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('archivedThresholdSaved')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
    }
  });
}

/**
 * AI-author the template description from the inputs the admin has filled in
 * so far. Toasts are owned by the caller (form dialog) so the generated text
 * can flow directly into the textarea — skip the global success toast.
 */
export function useGenerateOcrTemplateDescriptionMutation() {
  return useMutation({
    mutationFn: (data: GenerateDescriptionRequest) =>
      ocrTemplatesApi.generateDescription(data),
    meta: {
      operationType: 'create',
      skipGlobalSuccessToast: true
    }
  });
}

/**
 * Bulk-import templates from the workbook or a JSON file.
 *
 * `skipGlobalErrorToast` because a rejected file needs its per-row reasons
 * rendered in the dialog — a single toast can't say which of 40 rows failed or
 * why. The dialog owns the reporting; this hook just refreshes the list, since
 * a partial import still creates templates.
 */
export function useImportOcrTemplatesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocrTemplates');

  return useMutation<
    OcrTemplateImportResult,
    Error,
    { file: File; onProgress?: (n: number) => void }
  >({
    mutationFn: ({ file, onProgress }) =>
      ocrTemplatesApi.bulkImport(file, onProgress),
    meta: {
      operationType: 'create',
      skipGlobalErrorToast: true,
      fallbackSuccessMessage: t('importSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ocrTemplateKeys.all });
    }
  });
}
